import { Badge, Tabs, Tab, SxProps, Theme } from '@mui/material'
import { useTheme } from '@mui/styles'
import useButtonTabsStyles from '@styles/buttonTabs/buttonTabs'
import clsx from 'clsx'
import Link from 'next/link'
import React, {
  useEffect,
  useMemo,
  useState,
  ReactNode,
  ForwardRefRenderFunction
} from 'react'

// Variant enum
const VARIANTS = {
  filled: 'filled',
  standard: 'standard'
} as const

type VariantType = (typeof VARIANTS)[keyof typeof VARIANTS]

// Interface for tab item
interface TabItem {
  id?: string | number
  isVisible?: boolean
  link?: string
  badgeCount?: number
  startIcon?: React.ComponentType<{ sx?: SxProps<Theme> }>
  onClick?: () => void
  selected?: boolean
  text: ReactNode
}

// Props interface for ButtonTabs component
interface ButtonTabsProps {
  tabs: TabItem[]
  variant?: VariantType
  width?: string | number
  fontSize?: number
}

// Link component with forwardRef
interface LinkComponentProps {
  href: string
  [key: string]: any
}

const LinkComponent: ForwardRefRenderFunction<
  HTMLAnchorElement,
  LinkComponentProps
> = (props, ref) => {
  const { href, ...other } = props
  return (
    <Link href={href} passHref legacyBehavior>
      <a ref={ref} {...other} />
    </Link>
  )
}

const ForwardedLinkComponent = React.forwardRef(LinkComponent)

const ButtonTabs: React.FC<ButtonTabsProps> = ({
  tabs = [],
  variant = VARIANTS.standard,
  width = 'fit-content',
  fontSize = 13
}) => {
  const classesButtonTabs = useButtonTabsStyles()
  const theme = useTheme<Theme>()

  const defaultIndex = tabs.findIndex(tab => tab.selected) ?? 0

  const [value, setValue] = useState<number>(defaultIndex)

  useEffect(() => {
    setValue(defaultIndex)
  }, [defaultIndex])

  const classnameSelected = useMemo(
    () =>
      variant === VARIANTS.filled
        ? classesButtonTabs.detailsSelected
        : classesButtonTabs.detailsSelectedStandard,
    [
      variant,
      classesButtonTabs.detailsSelected,
      classesButtonTabs.detailsSelectedStandard
    ]
  )

  return (
    <Tabs
      variant="scrollable"
      value={variant === VARIANTS.standard ? value : false}
      aria-label="nav tabs"
      allowScrollButtonsMobile
      sx={{
        borderBottom: (t: Theme) => `1px solid ${t.palette.other.stroke}`,
        width: width
      }}
    >
      {tabs.map((tab, i) => {
        const Icon = tab.startIcon

        return (
          (tab.isVisible || tab.isVisible === undefined) &&
          (tab.link ? (
            <Tab
              key={tab.id || i}
              href={tab.link}
              className={clsx(classesButtonTabs.detailsRoot, {
                [classnameSelected]: tab.selected || false
              })}
              LinkComponent={ForwardedLinkComponent}
              disableRipple={variant === VARIANTS.standard}
              label={
                <div style={{ display: 'flex', gap: 5, padding: 2 }}>
                  {Icon && <Icon sx={{ fontSize: 15 }} />}
                  {tab.badgeCount ? (
                    <Badge
                      badgeContent={tab.badgeCount ?? 0}
                      color="primary"
                      sx={{
                        fontSize: 12,
                        minWidth: 0,
                        marginRight: 5,
                        '& .MuiBadge-badge': {
                          fontSize: 12,
                          backgroundColor: theme.palette.primary.light,
                          color: theme.palette.primary.main,
                          fontWeight: 800,
                          borderRadius: '8px',
                          right: '-20px',
                          top: '8px'
                        }
                      }}
                    >
                      {tab.text}
                    </Badge>
                  ) : (
                    tab.text
                  )}
                </div>
              }
            />
          ) : (
            <Tab
              key={tab.id || i}
              className={clsx(classesButtonTabs.detailsRoot, {
                [classnameSelected]: tab.selected || false
              })}
              value={i}
              disableRipple={variant === VARIANTS.standard}
              onClick={() => {
                tab.onClick?.()
                setValue(i)
              }}
              label={
                <div
                  style={{
                    display: 'flex',
                    gap: 5,
                    padding: 2,
                    fontSize: fontSize
                  }}
                >
                  {Icon && <Icon sx={{ fontSize: 15 }} />}
                  {tab.badgeCount ? (
                    <Badge
                      badgeContent={tab.badgeCount ?? 0}
                      color="primary"
                      sx={{
                        fontSize: 12,
                        minWidth: 0,
                        marginRight: 5,
                        '& .MuiBadge-badge': {
                          fontSize: 12,
                          backgroundColor: theme.palette.primary.light,
                          color: theme.palette.primary.main,
                          fontWeight: 800,
                          borderRadius: '8px',
                          right: '-20px',
                          top: '8px'
                        }
                      }}
                    >
                      {tab.text}
                    </Badge>
                  ) : (
                    tab.text
                  )}
                </div>
              }
            />
          ))
        )
      })}
    </Tabs>
  )
}

export default ButtonTabs
